/**
 * SortPanelSuperAdmin
 * -------------------
 * This component is responsible for:
 * 1. Taking Start Date & End Date from the user (IST via <input type="date">)
 * 2. Converting those dates into a UTC range compatible with backend filtering
 * 3. Calling the export API and downloading the Excel file
 *
 * IMPORTANT:
 * - UI dates are in IST (local time)
 * - Backend stores & filters dates in UTC
 * - Conversion logic is handled by `getCreatedAtUTC`
 */
import { useEffect, useState, useRef, FunctionComponent } from "react";
import styles from "../AddUsers/AddUsers.module.css";
import { connect, useDispatch } from "react-redux";
import { showSnackbarAction } from "../../Redux/actions";
import { useLocation } from "react-router-dom";
import { themeColors } from "../../Components/theme";
import axios from "axios";
import { url } from "../../Values/constants";
import 'react-phone-input-2/lib/style.css';
import "../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import StyledSubmitButton from "../../Components/Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import InputTextDate from "../../Components/TextInput/InputTextDate";

/**
 * Utility function:
 * Converts IST date range → UTC date range
 *
 * Example:
 * startDate = "2025-12-18"
 * endDate   = "2025-12-18"
 *
 * Output:
 * [
 *   "2025-12-17T18:30:00.000Z",
 *   "2025-12-18T18:29:59.999Z"
 * ]
 */
import { getCreatedAtUTC } from "../../Services/contacts";


type props = {
  user: any;
  history: any;
  organization: any;
  organizationId: string;
  organizationUsers: any[];
  branchList: any[];
  theme: any;
  authToken: string;
};

const SortPanelSuperAdmin: FunctionComponent<props> = ({
  history,
  organization,
  organizationId,
  organizationUsers,
  branchList,
  theme,
  authToken
}) => {
  const dispatcher = useDispatch();
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const location: any = useLocation();

  /**
 * startDate & endDate are stored as:
 * "YYYY-MM-DD" (IST, from <input type="date">)
 */
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [load, setLoad] = useState(false);

  /**
 * Warn user before page refresh / navigation
 */
  useEffect(() => {
    window.onbeforeunload = function () {
      return "Are you really want to perform the action?";
    };
  }, []);


  /**
 * Validates date input
 */
  const handleCheck = () => {
    if (startDate === "") {
      dispatcher(showSnackbarAction("Enter Start Date!!", "error"));
      return false;
    } else if (endDate === "") {
      dispatcher(showSnackbarAction("Enter End Date!!", "error"));
      return false;
    } else {
      return true;
    }
  };

  /**
   * Submit handler
   * ----------------
   * 1. Validates dates
   * 2. Converts IST → UTC using getCreatedAtUTC
   * 3. Sends API request
   * 4. Downloads Excel file
   */
  const create = async (e: any) => {
    e.preventDefault();
    const val = handleCheck();
    if (!val) {
      return;
    }

    /**
   * Convert IST date range → UTC date range
   */
    const [startUTC, endUTC] = getCreatedAtUTC(startDate, endDate);

    /**
     * API payload
     * NOTE:
     * Backend expects UTC dates for filtering
     */
    const apiData = {
      startDate: startUTC,
      endDate: endUTC,
      sort: { created_at: "-1" },
      filter: {
        transfer_status: [false]
      },
    };
    setLoad(true);
    try {
      axios.defaults.headers.common["x-access-token"] = authToken;
      const res = await axios.post(url + "/leads/masterSortSearch", apiData, {
        responseType: "blob",
      });

      /**
     * Download Excel file
     */
      const blob = new Blob([res.data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = `Leads_${new Date().toISOString().split("T")[0]}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);
      setLoad(false);
    } catch (e) {
      setLoad(false);
      console.error("Error while fetching sorted leads:", e);
    }
  };
  
  /**
   * Theme styles
   */
  const containerStyles = { backgroundColor: theme ? themeColors.backgroundColor : themeColors.backgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }
  const cardContainerStyles = { backgroundColor: theme ? themeColors.cardBackgroundColor : themeColors.cardBackgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }

  // ================================
  // Render
  // ================================
  return (
    <>
      <div className={styles.parent} style={{ ...containerStyles, marginTop: isMobile ? "6%" : "4%", fontFamily: "var(--font-family-main)" }}>
        {isMobile && (
          <div
            className="d-flex flex-row w-100 px-4 pt-4"
          >
            <h5
              style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-large)" }}
            >
              Sorted Leads
            </h5>
          </div>
        )}
        <div
          className="w-100 d-flex flex-column gap-2 px-4"
          style={{
            padding: isMobile ? '0' : '',
            maxWidth: isMobile ? '100vw' : undefined,
            margin: isMobile ? '0' : '0 auto',
            overflowY: isMobile ? 'auto' : undefined,
            maxHeight: isMobile ? 'calc(100vh - 120px)' : undefined,
          }}
        >
          <form
            className={`card shadow rounded mt-2${!isMobile ? ` ${styles.forcePadding}` : ""}`}
            style={{
              ...cardContainerStyles,
              padding: isMobile ? '16px' : '32px',
              boxSizing: 'border-box',
              minHeight: isMobile ? 'auto' : undefined,
              marginBottom: isMobile ? '50px' : 0,
            }}
          >
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12} sm={6}>
                <InputTextDate
                  type="date"
                  title="Start Date"
                  value={startDate}
                  onChange={(e: any) => setStartDate(e.target.value)}
                  required={true}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputTextDate
                  type="date"
                  title="End Date"
                  value={endDate}
                  onChange={(e: any) => setEndDate(e.target.value)}
                  required={true}
                />
              </Grid>
              {/* Remove submit button from inside form for mobile */}
              {!isMobile && (
                <Grid item xs={12} style={{ marginTop: 12 }}>
                  <StyledSubmitButton
                    onClick={(e) => create(e)}
                    themeMode={themeMUI.palette.mode}
                  >
                    {load ? (
                      <span style={{ display: "flex", justifyContent: "center" }}>
                        <Circles color="#fff" height={20} width={20} />Submit...
                      </span>
                    ) : (
                      <>Submit</>
                    )}
                  </StyledSubmitButton>
                </Grid>
              )}
            </Grid>
          </form>
        </div>
        {isMobile && (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: "white",
              p: 2,
              borderTop: "1px solid #ddd",
              boxShadow: "0 -2px 6px rgba(0,0,0,0.1)",
              zIndex: 100,
            }}
          >
            <StyledSubmitButton
              onClick={(e) => create(e)}
              themeMode={themeMUI.palette.mode}
              isMobile={isMobile}
            >
              {load ? (
                <span style={{ display: "flex", justifyContent: "center" }}>
                  <Circles color="#fff" height={20} width={20} />Submit
                </span>
              ) : (
                <>Submit</>
              )}
            </StyledSubmitButton>
          </Box>
        )}
      </div>
    </>
  );
};

// ================================
// Redux Mapping
// ================================
const mapStateToProps = (state: any) => {
  return {
    organization: state.organization,
    organizationId: state.organization.id,
    organizationUsers: state.organizationUsers.data,
    branchList: state.branchList.data,
    theme: state.theme.isLightMode,
    authToken: state.user.authToken,
  };
};

export default connect(mapStateToProps)(SortPanelSuperAdmin);
